import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "../AdminShell";
import ConfirmModal from "../ConfirmModal";
import { useAdminToast } from "../AdminToast";
import "./admin-live-dashboard.css";

/**
 * RAdminLiveDashboard — /admin/live-dashboard
 *
 * Redesigned operational tour-day screen, visuals per
 * design-ref/admin/Live Dashboard.html (+ .logic.js); functionality replicated
 * from the legacy frontend/src/pages/AdminLiveDashboardPage.js (2131 lines):
 *
 *   - GET  /api/admin/bookings/live-dashboard/{date}  (cache-busted, no-store,
 *          10s timeout, 401 → refresh-token + retry once → session-expired
 *          banner + redirect after 2s)
 *   - POST /api/admin/refresh-token       (on mount + every 12h, retry guard)
 *   - POST /api/admin/bookings/{id}/check-in           (toggle + confetti)
 *   - POST /api/admin/tours/manual-adjustment          (walk-in ±1)
 *   - GET  /api/admin/manual-tours/{date} · POST/PUT/DELETE /api/admin/manual-tours[/{id}]
 *   - GET  /api/admin/staff-assignments/{date} · POST /api/admin/staff-assignments
 *
 * Real-time plumbing preserved exactly: 3s recursive-timeout auto-refresh
 * (≥2.5s throttle, Safari-friendly), refresh on tab visibility/focus,
 * online/offline listeners keeping last-good data, Screen Wake Lock,
 * Adelaide-timezone default date. Uses AdminShell chromeless mode with its
 * own unified 100vh header per the design.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ------------------------------------------------------------- constants -- */

// Time options (8AM–6PM in 15-min increments) — same grid as the legacy page
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let hour = 8; hour <= 18; hour++) {
    for (const minute of [0, 15, 30, 45]) {
      if (hour === 18 && minute > 0) break;
      const time24 = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
      const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const ampm = hour >= 12 ? "PM" : "AM";
      opts.push({
        value: time24,
        label: `${hour12}:${minute.toString().padStart(2, "0")} ${ampm}`,
      });
    }
  }
  return opts;
})();

// 13 tour types — values are the legacy API values, labels are the shared UI copy
const TOUR_TYPE_OPTIONS = [
  { value: "full_tour", label: "Full Tour" },
  { value: "tour_without_movie", label: "Tour without Movie" },
  { value: "cutting_demonstration", label: "Cutting Demo" },
  { value: "ext_town_tour", label: "Ext Town Tour" },
  { value: "town_tour", label: "Town Tour" },
  { value: "no_town_tour", label: "No Town Tour" },
  { value: "samc", label: "SAMC" },
  { value: "kanku", label: "Kanku" },
  { value: "linen", label: "Linen" },
  { value: "linen_with_towels", label: "Linen + Towels" },
  { value: "no_church", label: "No Church" },
  { value: "serbian_church", label: "Serbian Church" },
  { value: "catholic_church", label: "Catholic Church" },
];

const TYPE_LABELS = TOUR_TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

// Source colour system: Oasis = emerald, Noble = sky, Manual = purple
const SOURCE_META = {
  oasis: {
    title: "Oasis Coaches",
    color: "#10b981",
    ph: "Oasis Coaches",
    chip: "Oasis",
    chipBg: "#10b98122",
    chipFg: "#0f8a63",
  },
  noble: {
    title: "Noble Tours",
    color: "#0ea5e9",
    ph: "Noble Tours",
    chip: "Noble",
    chipBg: "#0ea5e922",
    chipFg: "#0284c7",
  },
  manual: {
    title: "Manual Entry",
    color: "#7c4dff",
    ph: "e.g. Ghan Rail Group",
    chip: "Manual",
    chipBg: "#7c4dff22",
    chipFg: "#7c4dff",
  },
};

// Confetti burst pieces — copied 1:1 from the design prototype
const CONFETTI_PIECES = [
  { w: 9, h: 14, bg: "#f15a24", dur: 0.85, dx: -70, dy: -90, rot: 220 },
  { w: 8, h: 12, bg: "#1bab88", dur: 0.8, dx: 60, dy: -100, rot: -260 },
  { w: 7, h: 11, bg: "#18bfc6", dur: 0.9, dx: -110, dy: -40, rot: 180 },
  { w: 9, h: 13, bg: "#2596c9", dur: 0.75, dx: 100, dy: -55, rot: -200 },
  { w: 7, h: 10, bg: "#f4a06b", dur: 0.95, dx: -40, dy: -130, rot: 320 },
  { w: 8, h: 12, bg: "#e0a92b", dur: 0.85, dx: 35, dy: -135, rot: -300 },
  { w: 7, h: 11, bg: "#7c4dff", dur: 0.8, dx: 130, dy: -20, rot: 240 },
  { w: 8, h: 12, bg: "#1bab88", dur: 0.9, dx: -130, dy: 10, rot: -180 },
  { w: 7, h: 10, bg: "#f15a24", dur: 0.7, dx: 80, dy: 30, rot: 160 },
  { w: 8, h: 12, bg: "#18bfc6", dur: 0.8, dx: -80, dy: 40, rot: -220 },
  { w: 9, h: 13, bg: "#2596c9", dur: 0.85, dx: 15, dy: -150, rot: 280 },
  { w: 7, h: 11, bg: "#f4a06b", dur: 0.75, dx: -15, dy: 60, rot: -160 },
];

const EMPTY_MANUAL_ENTRY = () => ({
  company_name: "",
  date: getAdelaideDate(),
  time: "",
  number_of_people: "",
  crew_name: "",
  crew_no: "",
  staff_member: "",
  umoona_staff: "",
  notes: "",
  tour_source: "",
  tour_types: [],
  extra_tour_reason: "",
});

/* --------------------------------------------------------------- helpers -- */

// Current date in Adelaide timezone (YYYY-MM-DD) — same as legacy
function getAdelaideDate() {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Adelaide",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(now);
  const year = parts.find((p) => p.type === "year").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${year}-${month}-${day}`;
}

function getAdelaideTime() {
  return new Date().toLocaleTimeString("en-AU", {
    timeZone: "Australia/Adelaide",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// The backend serialises datetimes as naive UTC strings with no timezone
// suffix (e.g. "2026-07-01T23:35:10.088000"). new Date() would read a
// suffix-less datetime as LOCAL time, so an Adelaide-formatted result would be
// double-shifted. Append "Z" when there's no offset so it's parsed as UTC.
function parseServerDate(ts) {
  if (!ts) return null;
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(ts);
  return new Date(hasTz ? ts : `${ts}Z`);
}

// "Wednesday, 1 July 2026" — Adelaide-safe parse of a YYYY-MM-DD string
function formatDate(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-AU", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatTimeDisplay(time) {
  if (!time) return "Not set";
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
}

function formatDateDisplay(dateString) {
  if (!dateString) return "Not set";
  const date = new Date(dateString + "T00:00:00");
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

// "2 Adults, 1 Child" — same labels/pluralisation as the legacy page
function formatTicketBreakdown(ticketsBreakdown) {
  if (!ticketsBreakdown || ticketsBreakdown.length === 0) return null;
  const ticketLabels = {
    adult: "Adult",
    child: "Child",
    family_3: "Family (3)",
    family_4: "Family (4)",
    family_5: "Family (5)",
    family_6: "Family (6)",
    family_7: "Family (7)",
    family_8: "Family (8)",
  };
  return ticketsBreakdown
    .map((ticket) => {
      const label = ticketLabels[ticket.type] || ticket.type;
      const qty = ticket.quantity;
      const displayLabel =
        (ticket.type === "adult" || ticket.type === "child") && qty > 1
          ? `${label}s`
          : label;
      return `${qty} ${displayLabel}`;
    })
    .join(", ");
}

// Capacity meter colour: green → amber → red as the slot fills
function capacityColor(used, total = 30) {
  const pct = total > 0 ? used / total : 0;
  if (pct >= 0.9) return "#c0392b";
  if (pct >= 0.6) return "#e0a92b";
  return "#1bab88";
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/* ------------------------------------------------------------- component -- */

const RAdminLiveDashboard = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("online"); // online | offline
  const [lastUpdated, setLastUpdated] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [selectedDate, setSelectedDate] = useState(() => getAdelaideDate());

  // Manual tour logging state
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [selectedTourSource, setSelectedTourSource] = useState(null); // 'oasis' | 'noble' | 'manual'
  const [manualEntry, setManualEntry] = useState(EMPTY_MANUAL_ENTRY);
  const [submittingManualEntry, setSubmittingManualEntry] = useState(false);
  const [manualTours, setManualTours] = useState([]);

  // Row-level inline editing (design pattern: Edit → whole row becomes inputs)
  const [rowEditId, setRowEditId] = useState(null);
  const [rowDraft, setRowDraft] = useState(null);
  const [savingRow, setSavingRow] = useState(false);

  // Delete-with-named-confirm
  const [deletingTour, setDeletingTour] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Staff assignment state
  const [staffAssignments, setStaffAssignments] = useState({});
  const [editingStaff, setEditingStaff] = useState(null); // timeSlot being edited
  const [tempStaffName, setTempStaffName] = useState("");

  // Confetti burst + per-column "N more guests" scroll meta
  const [burst, setBurst] = useState(null); // { x, y, key }
  const [listMeta, setListMeta] = useState({}); // { [timeSlot]: { moreBelow, hiddenCount } }

  const tokenRefreshingRef = useRef(false);
  const burstTimerRef = useRef(null);
  const listRefs = useRef({});

  /* ------------------------------------------------------ auth plumbing -- */

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return null;
    }
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    };
  };

  const addCacheBuster = (url) => {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}_t=${Date.now()}`;
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  // Refresh authentication token to keep login alive indefinitely
  const refreshAuthToken = async () => {
    if (tokenRefreshingRef.current) return false; // prevent concurrent refreshes
    try {
      tokenRefreshingRef.current = true;
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin/login");
        return false;
      }
      const response = await fetch(
        `${BACKEND_URL}/api/admin/refresh-token`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("adminToken", data.access_token);
        return true;
      }
      if (response.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin/login");
        return false;
      }
      return false;
    } catch (err) {
      console.error("Error refreshing token:", err);
      return false;
    } finally {
      tokenRefreshingRef.current = false;
    }
  };

  // Fetch with timeout (Safari-friendly)
  const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === "AbortError") {
        throw new Error("Request timeout - please check your connection");
      }
      throw err;
    }
  };

  /* ------------------------------------------------------------ fetchers -- */

  const fetchDashboardData = async (showLoading = true, retryCount = 0) => {
    try {
      if (showLoading) setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const url = addCacheBuster(
        `${BACKEND_URL}/api/admin/bookings/live-dashboard/${selectedDate}`
      );
      const response = await fetchWithTimeout(
        url,
        { headers, cache: "no-store" },
        10000
      );

      if (response.status === 401) {
        if (retryCount === 0) {
          // Token expired — try to refresh and retry once
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            return await fetchDashboardData(showLoading, 1);
          }
        }
        setError("Your session has expired. Redirecting to login...");
        localStorage.removeItem("adminToken");
        setTimeout(() => {
          navigate("/admin/login");
        }, 2000);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data (${response.status})`);
      }

      const data = await response.json();
      setDashboardData(data);
      setError("");
      setLastUpdated(new Date());
    } catch (err) {
      console.error("fetchDashboardData error:", err);
      setError(err.message);
      // Keep showing last good data on error
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchManualTours = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const url = addCacheBuster(
        `${BACKEND_URL}/api/admin/manual-tours/${selectedDate}`
      );
      const response = await fetchWithTimeout(
        url,
        { headers, cache: "no-store" },
        10000
      );
      if (response.status === 401) {
        setError("Your session has expired. Redirecting to login...");
        localStorage.removeItem("adminToken");
        setTimeout(() => {
          navigate("/admin/login");
        }, 2000);
        return;
      }
      if (response.ok) {
        const tours = await response.json();
        setManualTours(tours || []);
      }
      // On non-OK keep showing last good data
    } catch (err) {
      console.error("Error fetching manual tours:", err);
    }
  };

  const fetchStaffAssignments = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const url = addCacheBuster(
        `${BACKEND_URL}/api/admin/staff-assignments/${selectedDate}`
      );
      const response = await fetchWithTimeout(
        url,
        { headers, cache: "no-store" },
        10000
      );
      if (response.status === 401) {
        setError("Your session has expired. Redirecting to login...");
        localStorage.removeItem("adminToken");
        setTimeout(() => {
          navigate("/admin/login");
        }, 2000);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setStaffAssignments(data.assignments || {});
      }
    } catch (err) {
      console.error("Error fetching staff assignments:", err);
    }
  };

  /* -------------------------------------------------------------- effects -- */

  // Auto refresh every 3 seconds — Safari/iOS-compatible recursive timeout
  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    let lastRefreshTime = Date.now();

    const refreshData = async () => {
      if (!isMounted) return;
      const now = Date.now();
      if (now - lastRefreshTime >= 2500) {
        lastRefreshTime = now;
        try {
          await Promise.allSettled([
            fetchDashboardData(false),
            fetchManualTours(),
            fetchStaffAssignments(),
          ]);
        } catch (err) {
          console.error("Refresh error:", err);
        }
      }
      if (isMounted) {
        timeoutId = setTimeout(refreshData, 3000);
      }
    };

    timeoutId = setTimeout(refreshData, 3000);

    // Safari pauses timers in background tabs — refresh on visibility/focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastRefreshTime = 0;
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(refreshData, 100);
      }
    };
    const handleFocus = () => {
      lastRefreshTime = 0;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(refreshData, 100);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [selectedDate]);

  // Fetch manual tours + staff assignments when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchManualTours();
      fetchStaffAssignments();
    }
  }, [selectedDate]);

  // Monitor network connection status — keep last-good data while offline
  useEffect(() => {
    const handleOnline = () => {
      setConnectionStatus("online");
      setError("");
      fetchDashboardData(false);
      fetchManualTours();
      fetchStaffAssignments();
    };
    const handleOffline = () => {
      setConnectionStatus("offline");
      setError("No internet connection - showing last loaded data");
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    if (!navigator.onLine) setConnectionStatus("offline");
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Screen Wake Lock — keep the tablet awake all day at the mine entrance
  useEffect(() => {
    let wakeLock = null;
    const requestWakeLock = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
          wakeLock.addEventListener("release", () => {});
        }
      } catch (err) {
        // Wake Lock not available — non-fatal
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && wakeLock === null) {
        requestWakeLock();
      }
    };
    requestWakeLock();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (wakeLock) {
        wakeLock.release().then(() => {
          wakeLock = null;
        });
      }
    };
  }, []);

  // Auto-refresh token on mount + every 12 hours to keep login alive
  useEffect(() => {
    refreshAuthToken();
    const tokenRefreshInterval = setInterval(() => {
      refreshAuthToken();
    }, 12 * 60 * 60 * 1000);
    return () => clearInterval(tokenRefreshInterval);
  }, []);

  // Initial + date-change dashboard fetch
  useEffect(() => {
    fetchDashboardData();
  }, [selectedDate]);

  // Confetti cleanup on unmount
  useEffect(() => () => clearTimeout(burstTimerRef.current), []);

  /* --------------------------------------- "N more guests ↓" measurement -- */

  const measureList = (slot, el) => {
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = remaining < 12;
    const hidden = Math.max(0, Math.round(remaining / 96));
    setListMeta((prev) => {
      const cur = prev[slot] || {};
      const moreBelow = !atBottom;
      if (cur.moreBelow === moreBelow && cur.hiddenCount === hidden) return prev;
      return { ...prev, [slot]: { moreBelow, hiddenCount: hidden } };
    });
  };

  useEffect(() => {
    const remeasure = () => {
      Object.entries(listRefs.current).forEach(([slot, el]) =>
        measureList(slot, el)
      );
    };
    const raf = requestAnimationFrame(remeasure);
    window.addEventListener("resize", remeasure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", remeasure);
    };
  }, [dashboardData]);

  /* -------------------------------------------------------- slot actions -- */

  // Check-in toggle (backend toggles) + confetti celebration on check-in
  const handleCheckIn = async (customer, event) => {
    const bookingId = customer.booking_id;
    const customerName = customer.customer_name;
    if (checkingIn.has(bookingId)) return;

    const willCheckIn = !customer.checked_in;
    let burstPos = null;
    if (willCheckIn && event && event.currentTarget) {
      const r = event.currentTarget.getBoundingClientRect();
      burstPos = {
        x: r.left + r.width / 2,
        y: r.top + r.height / 2,
        key: Date.now(),
      };
    }

    try {
      setCheckingIn((prev) => new Set(prev).add(bookingId));
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}/check-in`,
        { method: "POST", headers }
      );
      if (!response.ok) {
        throw new Error("Failed to update check-in status");
      }

      if (willCheckIn && burstPos && !prefersReducedMotion()) {
        setBurst(burstPos);
        clearTimeout(burstTimerRef.current);
        burstTimerRef.current = setTimeout(() => setBurst(null), 1000);
      }

      await fetchDashboardData(false);
    } catch (err) {
      toast.error(`Failed to check in ${customerName}: ${err.message}`);
    } finally {
      setCheckingIn((prev) => {
        const next = new Set(prev);
        next.delete(bookingId);
        return next;
      });
    }
  };

  // Walk-in ± adjustment
  const handleManualAdjustment = async (timeSlot, adjustment, bookingSource = "walkin") => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await fetch(
        `${BACKEND_URL}/api/admin/tours/manual-adjustment`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            tour_date: selectedDate,
            tour_time: timeSlot,
            adjustment,
            booking_source: bookingSource,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to adjust tickets");
      }
      await fetchDashboardData(false);
    } catch (err) {
      toast.error(`Failed to adjust tickets: ${err.message}`);
    }
  };

  /* ------------------------------------------------------ staff handlers -- */

  const handleEditStaff = (timeSlot) => {
    setEditingStaff(timeSlot);
    setTempStaffName(staffAssignments[timeSlot] || "");
  };

  const handleSaveStaff = async (timeSlot) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await fetch(
        `${BACKEND_URL}/api/admin/staff-assignments`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            tour_date: selectedDate,
            time_slot: timeSlot,
            staff_name: tempStaffName,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to save staff assignment");
      }
      setStaffAssignments({ ...staffAssignments, [timeSlot]: tempStaffName });
      setEditingStaff(null);
      setTempStaffName("");
      await fetchStaffAssignments();
    } catch (err) {
      toast.error(`Failed to save staff assignment: ${err.message}`);
    }
  };

  const handleCancelStaffEdit = () => {
    setEditingStaff(null);
    setTempStaffName("");
  };

  /* ------------------------------------------------- manual-log handlers -- */

  const handleSelectTourSource = (source) => {
    setSelectedTourSource(source);
    setManualEntry({ ...EMPTY_MANUAL_ENTRY(), tour_source: source });
    setShowManualEntryForm(true);
  };

  const handleCloseForm = () => {
    setShowManualEntryForm(false);
    setSelectedTourSource(null);
    setManualEntry(EMPTY_MANUAL_ENTRY());
  };

  const handleTourTypeChange = (tourType, checked) => {
    setManualEntry((prev) => ({
      ...prev,
      tour_types: checked
        ? [...prev.tour_types, tourType]
        : prev.tour_types.filter((t) => t !== tourType),
    }));
  };

  // Drawer submit — same validation + payload + endpoint as the legacy page
  const handleManualEntrySubmit = async (e) => {
    e.preventDefault();

    if (!manualEntry.date || !manualEntry.time || !manualEntry.number_of_people) {
      toast.error("Please fill in all required fields for manual tour entry");
      return;
    }
    if (!selectedTourSource) {
      toast.error("Please select a tour source (Oasis, Noble, or Manual)");
      return;
    }
    if (selectedTourSource === "manual" && !manualEntry.extra_tour_reason) {
      toast.error("Please provide a reason for this extra tour");
      return;
    }

    setSubmittingManualEntry(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const payload = {
        company_name: manualEntry.company_name || null,
        tour_date: manualEntry.date,
        tour_time: manualEntry.time,
        guest_count: parseInt(manualEntry.number_of_people, 10),
        crew_name: manualEntry.crew_name || null,
        crew_no: manualEntry.crew_no || null,
        tour_source: selectedTourSource,
        tour_types: manualEntry.tour_types || [],
        extra_tour_reason:
          selectedTourSource === "manual" ? manualEntry.extra_tour_reason : null,
        umoona_staff: manualEntry.umoona_staff || null,
        staff_member: manualEntry.staff_member || "",
        notes: manualEntry.notes || "",
      };

      const response = await fetch(`${BACKEND_URL}/api/admin/manual-tours`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to log tour");
      }

      const entryDate = manualEntry.date;
      setManualEntry(EMPTY_MANUAL_ENTRY());
      setSelectedTourSource(null);
      setShowManualEntryForm(false);

      await fetchManualTours();
      if (entryDate === selectedDate) {
        await fetchDashboardData(false);
      }
      toast.success("✓ Tour added to log");
    } catch (err) {
      toast.error(`Failed to log tour: ${err.message}`);
    } finally {
      setSubmittingManualEntry(false);
    }
  };

  // Row-level inline editing (design pattern; same PUT endpoint + body shape)
  const startRowEdit = (tour) => {
    setRowEditId(tour.id);
    setRowDraft({
      company_name: tour.company_name || "",
      tour_date: tour.tour_date,
      tour_time: tour.tour_time,
      guest_count:
        tour.guest_count != null ? tour.guest_count.toString() : "",
      crew_name: tour.crew_name || "",
      crew_no: tour.crew_no || "",
      umoona_staff: tour.umoona_staff || "",
      notes: tour.notes || "",
      tour_types: [...(tour.tour_types || [])],
    });
  };

  const cancelRowEdit = () => {
    setRowEditId(null);
    setRowDraft(null);
  };

  const setDraftField = (field, value) => {
    setRowDraft((prev) => ({ ...prev, [field]: value }));
  };

  const toggleDraftType = (typeValue) => {
    setRowDraft((prev) => ({
      ...prev,
      tour_types: prev.tour_types.includes(typeValue)
        ? prev.tour_types.filter((t) => t !== typeValue)
        : [...prev.tour_types, typeValue],
    }));
  };

  const saveRowEdit = async () => {
    if (rowEditId == null || !rowDraft) return;
    const tour = manualTours.find((t) => t.id === rowEditId);
    if (!tour) return;

    setSavingRow(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const parsedGuests = parseInt(rowDraft.guest_count, 10);
      const updateData = {
        company_name: rowDraft.company_name || "",
        tour_date: rowDraft.tour_date,
        tour_time: rowDraft.tour_time,
        guest_count: Number.isNaN(parsedGuests) ? tour.guest_count : parsedGuests,
        crew_name: rowDraft.crew_name || "",
        crew_no: rowDraft.crew_no || "",
        staff_member: tour.staff_member || "",
        umoona_staff: rowDraft.umoona_staff || "",
        notes: rowDraft.notes || "",
        tour_types: Array.isArray(rowDraft.tour_types) ? rowDraft.tour_types : [],
      };

      const response = await fetch(
        `${BACKEND_URL}/api/admin/manual-tours/${rowEditId}`,
        { method: "PUT", headers, body: JSON.stringify(updateData) }
      );
      if (!response.ok) {
        throw new Error("Failed to update tour");
      }

      await fetchManualTours();
      await fetchDashboardData(false);
      setRowEditId(null);
      setRowDraft(null);
      toast.success("✓ Tour entry updated");
    } catch (err) {
      toast.error(`Failed to update: ${err.message}`);
    } finally {
      setSavingRow(false);
    }
  };

  // Delete with named confirmation (design's "Delete "{company}"?" modal)
  const confirmDeleteTour = async () => {
    if (!deletingTour) return;
    setDeleteBusy(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const response = await fetch(
        `${BACKEND_URL}/api/admin/manual-tours/${deletingTour.id}`,
        { method: "DELETE", headers }
      );
      if (!response.ok) {
        throw new Error("Failed to delete manual tour");
      }
      setDeletingTour(null);
      await fetchManualTours();
      await fetchDashboardData(false);
      toast.success("Tour entry deleted");
    } catch (err) {
      setDeletingTour(null);
      toast.error(`Failed to delete manual tour: ${err.message}`);
    } finally {
      setDeleteBusy(false);
    }
  };

  /* --------------------------------------------------------------- render -- */

  const summary = (dashboardData && dashboardData.summary) || {};
  const updatedSecs = lastUpdated
    ? Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000))
    : null;
  const updatedLabel =
    updatedSecs == null
      ? "CONNECTING…"
      : updatedSecs <= 60
      ? `UPDATED ${updatedSecs}S AGO`
      : `UPDATED ${lastUpdated.toLocaleTimeString("en-AU", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}`;

  const sortedTours = [...manualTours].sort((a, b) => {
    const timeA = a.tour_time || "00:00";
    const timeB = b.tour_time || "00:00";
    return timeA.localeCompare(timeB);
  });

  const sessionExpired = !!error && error.includes("session has expired");
  const drawerMeta =
    (selectedTourSource && SOURCE_META[selectedTourSource]) || SOURCE_META.manual;

  const renderGuestCard = (customer) => {
    const busy = checkingIn.has(customer.booking_id);
    const breakdown = formatTicketBreakdown(customer.tickets_breakdown);
    return (
      <div className="ald-guest" key={customer.booking_id}>
        <div className="ald-guest-top">
          <div className="ald-guest-name">{customer.customer_name}</div>
          <span
            className={
              "ald-chip " +
              (customer.checked_in ? "ald-chip--in" : "ald-chip--pending")
            }
          >
            {customer.checked_in ? "Checked In" : "Pending"}
          </span>
        </div>
        <div className="ald-guest-meta">
          {customer.visiting_from && (
            <span className="ald-loc">📍 {customer.visiting_from}</span>
          )}
          <span className="ald-ref">{customer.booking_reference}</span>
        </div>
        {customer.customer_email && (
          <div className="ald-guest-email">{customer.customer_email}</div>
        )}
        <div className="ald-guest-tix">
          {breakdown || (
            <>
              {customer.guest_count} guests, {customer.ticket_count} tickets
            </>
          )}
        </div>
        <button
          type="button"
          key={`${customer.booking_id}-${customer.checked_in ? "on" : "off"}`}
          className={"ald-ci" + (customer.checked_in ? " is-on" : "")}
          disabled={busy}
          onClick={(e) => handleCheckIn(customer, e)}
        >
          {busy
            ? "Checking In…"
            : customer.checked_in
            ? "✓ Checked In"
            : "Check In"}
        </button>
        {customer.checked_in && customer.checked_in_at && (
          <div className="ald-ci-at">
            Checked in at{" "}
            {parseServerDate(customer.checked_in_at).toLocaleTimeString(
              "en-AU",
              { timeZone: "Australia/Adelaide" }
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSlotColumn = (timeSlot) => {
    const slotData = dashboardData.time_slots[timeSlot];
    if (!slotData) return null;
    const used = slotData.total_people || 0;
    const capColor = capacityColor(used, 30);
    const customers = slotData.customers || [];
    const meta = listMeta[timeSlot] || {};

    return (
      <div className="ald-slot" key={timeSlot}>
        <div className="ald-slot-head">
          <div className="ald-slot-time">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="2.5" />
              <path d="M7.5 7.5a6 6 0 000 9M16.5 7.5a6 6 0 010 9" />
            </svg>
            <span>{slotData.time_display}</span>
          </div>
          <span className="ald-slot-guests">GUESTS: {used}</span>
        </div>

        <div className="ald-slot-body">
          {/* STAFF assignment — click to assign, Enter/✓ saves */}
          {editingStaff === timeSlot ? (
            <div className="ald-staff is-editing">
              <span className="ald-mono-lbl">Staff</span>
              <input
                type="text"
                value={tempStaffName}
                autoFocus
                placeholder="Enter staff name"
                onChange={(e) => setTempStaffName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveStaff(timeSlot);
                  if (e.key === "Escape") handleCancelStaffEdit();
                }}
              />
              <button
                type="button"
                className="ald-staff-save"
                onClick={() => handleSaveStaff(timeSlot)}
                title="Save"
              >
                ✓
              </button>
              <button
                type="button"
                className="ald-staff-cancel"
                onClick={handleCancelStaffEdit}
                title="Cancel"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="ald-staff"
              onClick={() => handleEditStaff(timeSlot)}
            >
              <span className="ald-mono-lbl">Staff</span>
              <span
                className={
                  "ald-staff-name" +
                  (staffAssignments[timeSlot] ? "" : " is-empty")
                }
              >
                {staffAssignments[timeSlot] || "Click to assign staff"}
              </span>
            </button>
          )}

          {/* Booking-type badges */}
          <div className="ald-badges">
            <span className="ald-badge ald-badge--walkin">
              {slotData.walkin_tickets || 0} Walk-in
            </span>
            <span className="ald-badge ald-badge--online">
              {slotData.online_tickets || 0} Online
            </span>
          </div>

          {/* Walk-in controls */}
          <div className="ald-walkin">
            <button
              type="button"
              className="ald-minus"
              onClick={() => handleManualAdjustment(timeSlot, -1, "walkin")}
              disabled={slotData.walkin_tickets === 0}
              data-testid={`minus-walkin-${timeSlot}`}
              aria-label="Remove walk-in"
            >
              −
            </button>
            <button
              type="button"
              className="ald-add"
              onClick={() => handleManualAdjustment(timeSlot, 1, "walkin")}
              disabled={
                slotData.capacity &&
                slotData.capacity.used >= slotData.capacity.total
              }
              data-testid={`plus-walkin-${timeSlot}`}
            >
              + Add Walk-in
            </button>
          </div>
          <div className="ald-walkin-note">
            {slotData.capacity
              ? `✓ ${slotData.capacity.walkin_available} spots available`
              : `${30 - used} spots remaining`}
          </div>

          <div className="ald-mono-lbl ald-list-lbl">Online check-in</div>

          {/* Only this list scrolls; fade + "N more guests ↓" pill */}
          <div className="ald-listwrap">
            <div
              className="ald-list"
              ref={(el) => {
                listRefs.current[timeSlot] = el;
              }}
              onScroll={(e) => measureList(timeSlot, e.currentTarget)}
            >
              {customers.length === 0 ? (
                <div className="ald-empty-list">No Online Bookings</div>
              ) : (
                customers.map(renderGuestCard)
              )}
            </div>
            {meta.moreBelow && customers.length > 0 && (
              <div className="ald-more" aria-hidden="true">
                <div className="ald-more-fade" />
                <div className="ald-more-pill">
                  {meta.hiddenCount > 0
                    ? `${meta.hiddenCount} more guest${
                        meta.hiddenCount === 1 ? "" : "s"
                      }`
                    : "More below"}{" "}
                  <span>↓</span>
                </div>
              </div>
            )}
          </div>

          {/* Capacity meter N/30, green→amber→red */}
          <div className="ald-cap">
            <div className="ald-cap-row">
              <span className="ald-mono-lbl">Total capacity</span>
              <span className="ald-cap-num" style={{ color: capColor }}>
                {used} / 30
              </span>
            </div>
            <div className="ald-cap-bar">
              <div
                style={{
                  width: `${Math.min(100, (used / 30) * 100)}%`,
                  background: capColor,
                }}
              />
            </div>
            <div className="ald-cap-note">
              {Math.max(0, 30 - used)} spots remaining
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLogRow = (tour) => {
    const source = tour.tour_source || "manual";
    const srcMeta = SOURCE_META[source] || SOURCE_META.manual;
    const editing = rowEditId === tour.id;

    if (editing && rowDraft) {
      return (
        <tr key={tour.id} className="is-editing">
          <td className="ald-td-src">
            <span
              className="ald-srcchip"
              style={{ background: srcMeta.chipBg, color: srcMeta.chipFg }}
            >
              {srcMeta.chip}
            </span>
          </td>
          <td className="ald-td-company">
            <input
              className="ald-cell-input"
              value={rowDraft.company_name}
              placeholder="Company name"
              onChange={(e) => setDraftField("company_name", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-date">
            <input
              type="date"
              className="ald-cell-input"
              value={rowDraft.tour_date}
              onChange={(e) => setDraftField("tour_date", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-time">
            <select
              className="ald-cell-input"
              value={rowDraft.tour_time}
              onChange={(e) => setDraftField("tour_time", e.target.value)}
              disabled={savingRow}
            >
              {TIME_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </td>
          <td className="ald-td-guests">
            <input
              type="number"
              min="1"
              max="500"
              className="ald-cell-input ald-cell-input--num"
              value={rowDraft.guest_count}
              onChange={(e) => setDraftField("guest_count", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-types">
            <div className="ald-typechips">
              {TOUR_TYPE_OPTIONS.map((opt) => {
                const on = rowDraft.tour_types.includes(opt.value);
                return (
                  <button
                    type="button"
                    key={opt.value}
                    className={"ald-typechip" + (on ? " is-on" : "")}
                    onClick={() => toggleDraftType(opt.value)}
                    disabled={savingRow}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </td>
          <td className="ald-td-crew">
            <input
              className="ald-cell-input"
              value={rowDraft.crew_name}
              placeholder="Crew name"
              onChange={(e) => setDraftField("crew_name", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-crewno">
            <input
              className="ald-cell-input ald-cell-input--num"
              value={rowDraft.crew_no}
              placeholder="#"
              onChange={(e) => setDraftField("crew_no", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-staff">
            <input
              className="ald-cell-input"
              value={rowDraft.umoona_staff}
              placeholder="Umoona staff"
              onChange={(e) => setDraftField("umoona_staff", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-notes">
            <input
              className="ald-cell-input"
              value={rowDraft.notes}
              placeholder="Notes"
              onChange={(e) => setDraftField("notes", e.target.value)}
              disabled={savingRow}
            />
          </td>
          <td className="ald-td-actions">
            <button
              type="button"
              className="ald-rowbtn ald-rowbtn--save"
              onClick={saveRowEdit}
              disabled={savingRow}
            >
              {savingRow ? "Saving…" : "✓ Save"}
            </button>
            <button
              type="button"
              className="ald-rowbtn ald-rowbtn--cancel"
              onClick={cancelRowEdit}
              disabled={savingRow}
            >
              Cancel
            </button>
          </td>
        </tr>
      );
    }

    const typesLabel =
      tour.tour_types && tour.tour_types.length > 0
        ? tour.tour_types.map((t) => TYPE_LABELS[t] || t).join(" + ")
        : "—";
    const notesShort = tour.notes
      ? tour.notes.length > 30
        ? tour.notes.substring(0, 30) + "..."
        : tour.notes
      : "—";

    return (
      <tr key={tour.id}>
        <td className="ald-td-src">
          <span
            className="ald-srcchip"
            style={{ background: srcMeta.chipBg, color: srcMeta.chipFg }}
          >
            {srcMeta.chip}
          </span>
        </td>
        <td className="ald-td-company">{tour.company_name || "—"}</td>
        <td className="ald-td-date">{formatDateDisplay(tour.tour_date)}</td>
        <td className="ald-td-time">{formatTimeDisplay(tour.tour_time)}</td>
        <td className="ald-td-guests">{tour.guest_count}</td>
        <td className="ald-td-types">{typesLabel}</td>
        <td className="ald-td-crew">{tour.crew_name || "—"}</td>
        <td className="ald-td-crewno">{tour.crew_no || "—"}</td>
        <td className="ald-td-staff">{tour.umoona_staff || "—"}</td>
        <td className="ald-td-notes" title={tour.notes || ""}>
          {notesShort}
        </td>
        <td className="ald-td-actions">
          <button
            type="button"
            className="ald-rowbtn ald-rowbtn--edit"
            onClick={() => startRowEdit(tour)}
          >
            Edit
          </button>
          <button
            type="button"
            className="ald-rowbtn ald-rowbtn--del"
            onClick={() => setDeletingTour(tour)}
            title="Delete entry"
          >
            ✕
          </button>
        </td>
      </tr>
    );
  };

  return (
    <AdminShell chromeless pageClass="pg-admin-live-dashboard">
      <div className="pg-admin-live-dashboard">
        <div className="ald-page">
          {/* Session-expired banner (shown for 2s before the redirect) */}
          {sessionExpired && <div className="ald-expired">⚠ {error}</div>}

          {/* Unified 100vh header */}
          <header className="ald-header">
            <div className="ald-brand">
              <span className="ald-brand-tile">
                <img src="/design/assets/umoona-emblem.png" alt="" />
              </span>
              <div className="ald-brand-text">
                <div className="ald-brand-row">
                  <span className="ald-brand-name">LIVE TOUR DASHBOARD</span>
                  <span className="ald-live">
                    <span className="ald-live-dot" />
                    LIVE
                  </span>
                  {connectionStatus === "offline" && (
                    <span className="ald-statuspill ald-statuspill--offline">
                      Offline
                    </span>
                  )}
                  {connectionStatus === "online" && error && !sessionExpired && (
                    <span className="ald-statuspill ald-statuspill--issue">
                      Issue
                    </span>
                  )}
                </div>
                <div className="ald-brand-meta">
                  {formatDate(selectedDate).toUpperCase()} ·{" "}
                  <span className="ald-updated">{updatedLabel}</span>
                </div>
              </div>
            </div>

            <div className="ald-header-right">
              <div className="ald-stats">
                <div className="ald-stat">
                  <div className="ald-stat-lbl">Guests</div>
                  <div className="ald-stat-val ald-stat-val--guests">
                    {summary.total_people || 0}
                  </div>
                </div>
                <div className="ald-stat-div" />
                <div className="ald-stat">
                  <div className="ald-stat-lbl">Bookings</div>
                  <div className="ald-stat-val ald-stat-val--bookings">
                    {summary.total_bookings || 0}
                  </div>
                </div>
                <div className="ald-stat-div" />
                <div className="ald-stat ald-stat--checkedin">
                  <div className="ald-stat-lbl">Checked In</div>
                  <div className="ald-stat-val ald-stat-val--checkedin">
                    {summary.total_checked_in || 0}
                  </div>
                </div>
              </div>
              <div className="ald-controls">
                <input
                  type="date"
                  className="ald-date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
                <button
                  type="button"
                  className={"ald-refresh" + (loading ? " is-loading" : "")}
                  onClick={() => {
                    fetchDashboardData(true);
                    fetchManualTours();
                    fetchStaffAssignments();
                  }}
                  disabled={loading}
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 11a8 8 0 10-2 6" />
                    <path d="M20 4v5h-5" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="ald-logout"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            </div>
          </header>

          <main className="ald-main">
            {/* Persistent error strip (cleared by the next successful poll) */}
            {error && !sessionExpired && (
              <div className="ald-error">{error}</div>
            )}

            {loading ? (
              <div className="adm-loading">
                <div className="adm-spinner" />
                Loading dashboard…
              </div>
            ) : (
              <>
                {/* Three tour-slot columns — 100vh fit, only guest lists scroll */}
                {dashboardData && dashboardData.available_times.length > 0 && (
                  <div className="ald-slots">
                    {dashboardData.available_times.map(renderSlotColumn)}
                  </div>
                )}

                {/* No tours available (e.g. blacked-out date) */}
                {dashboardData && dashboardData.available_times.length === 0 && (
                  <div className="ald-notours">
                    <div className="ald-notours-icon">📅</div>
                    <div className="ald-notours-title">No Tours Available</div>
                    <div className="ald-notours-sub">
                      No underground mine tours are scheduled for{" "}
                      {formatDate(selectedDate)}
                    </div>
                  </div>
                )}

                {/* Manual Tour Log */}
                <section className="ald-log">
                  <div className="ald-log-head">
                    <div className="ald-log-title">Manual Tour Log</div>
                    <div className="ald-log-actions">
                      <span className="ald-sync">● LIVE SYNC (3s)</span>
                      <button
                        type="button"
                        className="ald-src ald-src--oasis"
                        onClick={() => handleSelectTourSource("oasis")}
                      >
                        + Oasis
                      </button>
                      <button
                        type="button"
                        className="ald-src ald-src--noble"
                        onClick={() => handleSelectTourSource("noble")}
                      >
                        + Noble
                      </button>
                      <button
                        type="button"
                        className="ald-src ald-src--manual"
                        onClick={() => handleSelectTourSource("manual")}
                      >
                        + Manual
                      </button>
                    </div>
                  </div>

                  {sortedTours.length === 0 ? (
                    <div className="ald-log-empty">
                      <div className="ald-log-empty-title">No Entries Yet</div>
                      <div className="ald-log-empty-sub">
                        No manual tour entries logged for{" "}
                        {formatDate(selectedDate)}.
                      </div>
                    </div>
                  ) : (
                    <div className="ald-tablewrap">
                      <table className="ald-table">
                        <thead>
                          <tr>
                            <th>Source</th>
                            <th>Company</th>
                            <th>Date</th>
                            <th>Time</th>
                            <th className="c">Guests</th>
                            <th>Tour Type</th>
                            <th>Crew</th>
                            <th className="c">Crew No.</th>
                            <th>Umoona Staff</th>
                            <th>Notes</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>{sortedTours.map(renderLogRow)}</tbody>
                      </table>
                    </div>
                  )}
                </section>

                <div className="ald-foot">
                  Dashboard refreshes automatically every 3 seconds · Last
                  updated: {getAdelaideTime()}
                </div>
              </>
            )}
          </main>
        </div>

        {/* CONFETTI BURST */}
        {burst && (
          <div
            className="ald-burst"
            key={burst.key}
            style={{ left: burst.x, top: burst.y }}
            aria-hidden="true"
          >
            {CONFETTI_PIECES.map((p, i) => (
              <span
                key={i}
                style={{
                  width: p.w,
                  height: p.h,
                  background: p.bg,
                  animationDuration: `${p.dur}s`,
                  "--dx": `${p.dx}px`,
                  "--dy": `${p.dy}px`,
                  "--rot": `${p.rot}deg`,
                }}
              />
            ))}
          </div>
        )}

        {/* DELETE LOG-ENTRY CONFIRM (named) */}
        <ConfirmModal
          open={!!deletingTour}
          title={
            deletingTour && deletingTour.company_name
              ? `Delete "${deletingTour.company_name}"?`
              : "Delete this tour entry?"
          }
          body="This tour entry will be removed from today's log. This cannot be undone."
          confirmLabel="Delete Entry"
          danger
          busy={deleteBusy}
          onConfirm={confirmDeleteTour}
          onCancel={() => setDeletingTour(null)}
        />

        {/* MANUAL TOUR ENTRY DRAWER */}
        {showManualEntryForm && selectedTourSource && (
          <div className="ald-drawer-root">
            <div className="ald-drawer-dim" onClick={handleCloseForm} />
            <div className="ald-drawer" role="dialog" aria-modal="true">
              <div
                className="ald-drawer-head"
                style={{ background: drawerMeta.color }}
              >
                <div className="ald-drawer-headtext">
                  <div className="ald-drawer-eyebrow">New manual tour</div>
                  <div className="ald-drawer-title">{drawerMeta.title}</div>
                </div>
                <button
                  type="button"
                  className="ald-drawer-close"
                  onClick={handleCloseForm}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <form className="ald-drawer-form" onSubmit={handleManualEntrySubmit}>
                <div className="ald-drawer-body">
                  <label className="ald-field">
                    <span className="ald-field-lbl">Company / Group</span>
                    <input
                      type="text"
                      value={manualEntry.company_name}
                      placeholder={drawerMeta.ph}
                      onChange={(e) =>
                        setManualEntry({
                          ...manualEntry,
                          company_name: e.target.value,
                        })
                      }
                    />
                  </label>

                  <div className="ald-field-grid">
                    <label className="ald-field">
                      <span className="ald-field-lbl">Date *</span>
                      <input
                        type="date"
                        value={manualEntry.date}
                        required
                        onChange={(e) =>
                          setManualEntry({ ...manualEntry, date: e.target.value })
                        }
                      />
                    </label>
                    <label className="ald-field">
                      <span className="ald-field-lbl">Time * (8am–6pm)</span>
                      <select
                        value={manualEntry.time}
                        required
                        onChange={(e) =>
                          setManualEntry({ ...manualEntry, time: e.target.value })
                        }
                      >
                        <option value="">Select time</option>
                        {TIME_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <label className="ald-field">
                    <span className="ald-field-lbl">Number of guests *</span>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      placeholder="e.g. 34"
                      value={manualEntry.number_of_people}
                      required
                      onChange={(e) =>
                        setManualEntry({
                          ...manualEntry,
                          number_of_people: e.target.value,
                        })
                      }
                    />
                  </label>

                  <div className="ald-typegrid-wrap">
                    <span className="ald-field-lbl">
                      Tour type *{" "}
                      <span className="ald-field-opt">
                        (select all that apply)
                      </span>
                    </span>
                    <div className="ald-typegrid">
                      {TOUR_TYPE_OPTIONS.map((opt) => {
                        const on = manualEntry.tour_types.includes(opt.value);
                        return (
                          <button
                            type="button"
                            key={opt.value}
                            className="ald-typeopt"
                            style={{
                              background: on ? `${drawerMeta.color}14` : "#fff",
                              borderColor: on ? drawerMeta.color : "#e5dfd3",
                              color: on ? "#2a2018" : "#54504a",
                            }}
                            onClick={() => handleTourTypeChange(opt.value, !on)}
                          >
                            <span
                              className="ald-typeopt-box"
                              style={{
                                borderColor: on ? drawerMeta.color : "#cfc7b8",
                                background: on ? drawerMeta.color : "#fff",
                              }}
                            >
                              {on ? "✓" : ""}
                            </span>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="ald-field-grid ald-field-grid--crew">
                    <label className="ald-field">
                      <span className="ald-field-lbl">
                        Crew / Bus{" "}
                        <span className="ald-field-opt">(optional)</span>
                      </span>
                      <input
                        type="text"
                        placeholder="e.g. Bus 14 · G. Yannie"
                        value={manualEntry.crew_name}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            crew_name: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label className="ald-field">
                      <span className="ald-field-lbl">Crew No.</span>
                      <input
                        type="text"
                        placeholder="#"
                        value={manualEntry.crew_no}
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            crew_no: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>

                  <label className="ald-field">
                    <span className="ald-field-lbl">Umoona staff on tour</span>
                    <input
                      type="text"
                      placeholder="Umoona staff member name"
                      value={manualEntry.umoona_staff}
                      onChange={(e) =>
                        setManualEntry({
                          ...manualEntry,
                          umoona_staff: e.target.value,
                        })
                      }
                    />
                  </label>

                  {selectedTourSource === "manual" && (
                    <label className="ald-field">
                      <span className="ald-field-lbl">
                        Reason for extra tour *
                      </span>
                      <textarea
                        rows="2"
                        placeholder="Why was this extra tour needed? (e.g., large group, special request)"
                        value={manualEntry.extra_tour_reason}
                        required
                        onChange={(e) =>
                          setManualEntry({
                            ...manualEntry,
                            extra_tour_reason: e.target.value,
                          })
                        }
                      />
                    </label>
                  )}

                  <label className="ald-field ald-field--last">
                    <span className="ald-field-lbl">
                      Notes <span className="ald-field-opt">(optional)</span>
                    </span>
                    <textarea
                      rows="2"
                      placeholder="Anything the guide should know…"
                      value={manualEntry.notes}
                      onChange={(e) =>
                        setManualEntry({ ...manualEntry, notes: e.target.value })
                      }
                    />
                  </label>
                </div>

                <div className="ald-drawer-foot">
                  <button
                    type="button"
                    className="ald-drawer-cancel"
                    onClick={handleCloseForm}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ald-drawer-submit"
                    style={{ background: drawerMeta.color }}
                    disabled={submittingManualEntry}
                  >
                    {submittingManualEntry ? "Saving…" : "Add to Tour Log"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default RAdminLiveDashboard;
